import { useEffect, useRef, useState } from "react";
import { draftStorageKey } from "../utils/triloChatConstants";

/**
 * Accept/edit decisions for the draft cards of the open session.
 *
 * Decisions are per session and local to the browser, so they are mirrored into
 * localStorage and restored when the session is reopened.
 *
 * @param {string|null} activeSessionId - Session the drafts belong to
 * @returns {Object} { draftStates, setDraftStates } keyed by "<msg>-<draft>"
 */
export const useTriloChatDrafts = activeSessionId => {
  const [draftStates, setDraftStates] = useState({});
  const draftSessionRef = useRef(null);

  // Keep a ref in sync with activeSessionId so the save effect can read it
  // without having activeSessionId as a dependency (which would cause it to fire
  // on session switch before the restore effect runs, overwriting saved data).
  useEffect(() => {
    draftSessionRef.current = activeSessionId;
  }, [activeSessionId]);

  // Restore draft states from localStorage when session changes
  useEffect(() => {
    if (!activeSessionId) {
      setDraftStates({});
      return;
    }
    try {
      const saved = localStorage.getItem(draftStorageKey(activeSessionId));
      setDraftStates(saved ? JSON.parse(saved) : {});
    } catch {
      setDraftStates({});
    }
  }, [activeSessionId]);

  // Persist draft states — depends only on draftStates, reads session from ref
  // so session switches don't trigger a save with stale data.
  useEffect(() => {
    const sid = draftSessionRef.current;
    if (!sid) return;
    localStorage.setItem(draftStorageKey(sid), JSON.stringify(draftStates));
  }, [draftStates]); // eslint-disable-line

  return { draftStates, setDraftStates };
};
